<script setup lang="ts">
import { InformationIcon } from '~/components/design-system/icons/index';
import { useSiteStore } from '~/stores/site.store';
import type { Modals } from '~/interfaces/dto/general/modals';
import Button from '~/components/design-system/button.vue';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import GenericLoader from '~/components/user-interface/generic-loader.vue';
import Tabs from '~/components/design-system/tabs/tabs.vue';
import Tab from '~/components/design-system/tabs/tab.vue';
import GenericError from '~/components/user-interface/generic-error.vue';
import { useResponsibleGamingInit } from '~/composables/useCheckRestrictions';

const { $t, $enabled, $trackEvent } = useNuxtApp();
const site = useSiteStore();
const activeTab = ref<string>();
const rg = useRgStore();
const loading = ref<boolean>(true);

const limitsEnabled = computed(
  () =>
    !rg.hasSelfExclusion &&
    !rg.currentCoolOff &&
    ($enabled('responsiblegaming.wagerlimit') ||
      $enabled('responsiblegaming.sessionlimit')),
);
const locksEnabled = computed(
  () =>
    $enabled('responsiblegaming.coolingoffperiod') ||
    $enabled('responsiblegaming.selfexclusion'),
);

const defaultTab = computed(() => {
  if (locksEnabled.value) return 'Exclusion';
  if (limitsEnabled.value) return 'Limits';
});

function openInfo(modalName: string) {
  site.activateModal(`rG${modalName}Message` as Modals);
}

const refreshLimits = () => {
  loading.value = true;
  useResponsibleGamingInit()
    .catch((e) => console.error(e))
    .finally(() => (loading.value = false));
};

provide('refreshResponsibleGaming', () => refreshLimits());

onMounted(() => {
  refreshLimits();
  activeTab.value = defaultTab.value;
  $trackEvent('Account', 'MY_ACCOUNT_SETTINGS', {
    to: 'responsible gaming',
  });
});
</script>

<template>
  <UserLimitedFunctionalityHolster
    notice-key="responsible-gaming"
    site-section="rg"
  >
    <div
      class="w-full sm:py-4 mb-8 flex justify-center text-base-priority sm:max-w-[428px] mx-auto"
    >
      <div
        class="bg-layer pb-2 pt-4 flex flex-col gap-1 sm:rounded-xl w-full relative overflow-hidden"
      >
        <GenericLoader v-show="loading" container />
        <div class="flex justify-between px-4 mb-2">
          <p class="my-0 font-bold text-lg">{{ $t('responsible-gambling') }}</p>
          <div class="flex">
            <Button
              type="transparent"
              class="mr-2"
              padding="sm"
              icon
              @click="refreshLimits()"
            >
              <ArrowPathIcon
                :class="loading ? 'animate-spin' : '-rotate-180'"
                class="w-5 transition-all"
              />
            </Button>
            <InformationIcon
              class="cursor-pointer mt-1"
              @click="openInfo(activeTab)"
            />
          </div>
        </div>
        <hr class="std-border-color" />

        <div class="card">
          <Tabs
            v-if="locksEnabled || limitsEnabled"
            :default-tab="defaultTab"
            alternate-bg-colors
            stretch
            @tab-change="(val) => (activeTab = val)"
          >
            <template #divider>
              <hr
                v-show="locksEnabled && limitsEnabled"
                class="std-border-color mt-1"
              />
            </template>
            <template #tabList>
              <Tab v-if="locksEnabled" id="Exclusion" :title="$t('exclusion')">
                <LazyAccountSettingsRestrictionsExclusion />
              </Tab>
              <Tab v-if="limitsEnabled" id="Limits" :title="$t('limits')">
                <LazyAccountSettingsRestrictionsLimits />
              </Tab>
            </template>
          </Tabs>
          <GenericError v-else state="danger">
            {{ $t('something-went-wrong') }}
          </GenericError>
        </div>
      </div>
    </div>
  </UserLimitedFunctionalityHolster>
</template>
